import { formatFilename, removeAccents } from "../../../../shared/export/utils";
import {
  aggregateMaterialDelivery,
  getParentCategoryLabelPlural,
} from "./utils";
import { REGULATION_TYPES } from "../../characterizationDetails/constants/regulation";

export const DECREE_1199_THRESHOLD_CM = 40;

const thresholdFilenameSuffix = (thresholdCm) =>
  `_seuil${Number(thresholdCm) || 0}cm`;

const formatPercent = (value, total) => {
  if (!total || !value) return "0.0%";
  const percent = (value / total) * 100;
  return `${Math.max(0, Math.min(100, percent)).toFixed(4)}%`;
};

const isOtherParentCategory = (cat, t) => {
  const raw = String(cat?.parentCategory || "");
  const translated = String(t(raw) || "").toLowerCase();
  const key = raw.toLowerCase();
  return (
    translated === "other" ||
    translated === "autres" ||
    key === "other" ||
    key === "autres"
  );
};

const toCsvHeaderToken = (label) =>
  removeAccents(String(label || ""))
    .toUpperCase()
    .replace(/\s+/g, "_");

const toCsvPercentHeader = (label) => `${toCsvHeaderToken(label)}_%`;

const toCsvKgHeader = (label) => `${toCsvHeaderToken(label)}_kg`;

const normalizeMaterialLookup = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();

const findCatalogMaterial = (referenceName, catalogByRef) => {
  if (!referenceName || !catalogByRef) return null;
  const direct = catalogByRef.get(referenceName);
  if (direct) return direct;

  const needle = normalizeMaterialLookup(referenceName);
  if (!needle) return null;

  for (const material of catalogByRef.values()) {
    const candidates = [
      material?.referenceName,
      material?.cls,
      ...(Array.isArray(material?.possibleNames) ? material.possibleNames : []),
    ];
    if (candidates.some((name) => normalizeMaterialLookup(name) === needle)) {
      return material;
    }
  }
  return null;
};

const getLocalizedMaterialLabel = (
  referenceName,
  t,
  catalogMaterial,
  locale,
) => {
  if (!referenceName) return "";

  const translated = t(referenceName, { defaultValue: "" });
  if (translated) return translated;

  const catalogKey = catalogMaterial?.referenceName;
  if (catalogKey && catalogKey !== referenceName) {
    const catalogTranslated = t(catalogKey, { defaultValue: "" });
    if (catalogTranslated) return catalogTranslated;
  }

  const lang = String(locale || "")
    .slice(0, 2)
    .toLowerCase();
  if (lang === "en") {
    return (
      catalogMaterial?.englishFormattedName ||
      catalogMaterial?.frenchFormattedName ||
      referenceName
    );
  }

  return (
    catalogMaterial?.frenchFormattedName ||
    catalogMaterial?.englishFormattedName ||
    referenceName
  );
};

const buildFinesHeaderName = (thresholdCm, t) => {
  const finesLabelText =
    thresholdCm > 0
      ? t("Fines + objects below {{threshold}} cm", {
          threshold: thresholdCm,
        })
      : t("Fines");
  return toCsvPercentHeader(finesLabelText);
};

const buildCsvStructureHeaders = (t) => ({
  mass: t("Mass"),
  volume: t("Volume"),
  objectCount: t("Number of objects"),
  total: t("Total"),
  date: t("Date"),
  producer: t("csvHeader.producer"),
  cutoffThresholdCm: t("csvHeader.cutoffThresholdCm"),
  recoverableMassPercent: t("csvHeader.recoverableMassPercent"),
  recoverableVolumePercent: t("csvHeader.recoverableVolumePercent"),
  summaryTable: t("csvHeader.summaryTableDecree1199"),
  massKg: t("csvHeader.massKg"),
});

/** OMR/RHW materials are prefixed hw_ ; the other regulations use the DIB catalog. */
const isHousewasteMaterial = (referenceName) =>
  String(referenceName).startsWith("hw_");

const isUnknownCsvMaterial = (referenceName) => {
  const normalized = String(referenceName || "")
    .trim()
    .toLowerCase();
  return normalized === "unknown" || normalized === "inconnu";
};

/** Keeps materials matching the export regulation (RHW includes unknown/inconnu buckets). */
const isMaterialAllowedForCsv = (referenceName, isHousewasteRegulation) => {
  const ref = String(referenceName || "").trim();
  if (!ref) return false;

  const isHw = isHousewasteMaterial(referenceName);
  if (isHw === isHousewasteRegulation) return true;
  return isHousewasteRegulation && isUnknownCsvMaterial(referenceName);
};

const buildStableMaterialCatalog = ({
  materialCatalog = [],
  materialDelivery = [],
  t,
  locale,
  isHousewasteRegulation = false,
}) => {
  const collator = new Intl.Collator(locale || "fr", {
    sensitivity: "base",
    numeric: true,
  });

  const catalogByRef = new Map();
  materialCatalog.forEach((material) => {
    const referenceName = material?.referenceName || material?.cls;
    if (!referenceName) return;
    catalogByRef.set(referenceName, material);
    catalogByRef.set(normalizeMaterialLookup(referenceName), material);
  });

  const byRef = new Map();

  materialCatalog.forEach((material) => {
    const referenceName = material?.referenceName || material?.cls;
    if (!referenceName) return;
    if (!isMaterialAllowedForCsv(referenceName, isHousewasteRegulation)) return;
    byRef.set(referenceName, {
      referenceName,
      label: getLocalizedMaterialLabel(referenceName, t, material, locale),
    });
  });

  materialDelivery.forEach((material) => {
    const referenceName = material?.referenceName || material?.cls;
    if (!referenceName || byRef.has(referenceName)) return;
    if (!isMaterialAllowedForCsv(referenceName, isHousewasteRegulation)) return;
    const catalogMaterial = findCatalogMaterial(referenceName, catalogByRef);
    byRef.set(referenceName, {
      referenceName,
      label: getLocalizedMaterialLabel(
        referenceName,
        t,
        catalogMaterial,
        locale,
      ),
    });
  });

  return Array.from(byRef.values()).sort((a, b) =>
    collator.compare(a.label || a.referenceName, b.label || b.referenceName),
  );
};

const findDeliveryMaterial = (deliveryByCls, referenceName) =>
  deliveryByCls.get(referenceName) || null;

const buildMaterialHeaderLabels = (stableMaterials) =>
  stableMaterials.map((material) => toCsvPercentHeader(material.label));

const insertFinesHeaders = ({
  percentHeaders,
  kgHeaders,
  parentCategories,
  t,
  hasFines,
}) => {
  if (!hasFines) {
    return { percentHeaders, kgHeaders };
  }

  const otherIndex = (() => {
    const idx = parentCategories.findIndex((cat) =>
      isOtherParentCategory(cat, t),
    );
    return idx !== -1 ? idx : parentCategories.length - 1;
  })();

  const finesLabel = t("csvHeader.otherFines");
  const nextPercent = [...percentHeaders];
  const nextKg = [...kgHeaders];
  nextPercent.splice(otherIndex + 1, 0, toCsvPercentHeader(finesLabel));
  nextKg.splice(otherIndex + 1, 0, toCsvKgHeader(finesLabel));
  return { percentHeaders: nextPercent, kgHeaders: nextKg };
};

const buildRecapCategoryHeaders = ({ parentCategories, t, hasFines }) => {
  const percentHeaders = parentCategories.map((cat) =>
    toCsvPercentHeader(getParentCategoryLabelPlural(t, cat.parentCategory)),
  );
  const kgHeaders = parentCategories.map((cat) =>
    toCsvKgHeader(getParentCategoryLabelPlural(t, cat.parentCategory)),
  );
  return insertFinesHeaders({
    percentHeaders,
    kgHeaders,
    parentCategories,
    t,
    hasFines,
  });
};

const buildMassTabHeaders = ({ parentCategories, t, hasFines }) => {
  const structure = buildCsvStructureHeaders(t);
  const { percentHeaders, kgHeaders } = buildRecapCategoryHeaders({
    parentCategories,
    t,
    hasFines,
  });
  return [
    structure.summaryTable,
    structure.cutoffThresholdCm,
    structure.recoverableMassPercent,
    ...percentHeaders,
    structure.massKg,
    ...kgHeaders,
  ];
};

/**
 * Builds the 4 CSV payloads for a single characterization report.
 * - mass / volume / count use clientThresholdCm (and clientThresholdData)
 * - TableauRecap uses decree 11-99 at 40 cm (decreeData)
 */
export const buildCharacterizationCsvFiles = ({
  period,
  container,
  t,
  locale,
  clientThresholdCm,
  clientThresholdData,
  decreeData,
  materialCatalog = [],
  regulationType,
}) => {
  const isHousewasteRegulation = regulationType === REGULATION_TYPES.RHW;
  const filenameBase = formatFilename(
    period?.start || period?._time,
    container?._id,
  );
  // period.weight is stored in kg
  const totalWeightKg = period?.weight ? Number(period.weight) : 0;
  const csvFiles = [];

  const clientDelivery = aggregateMaterialDelivery(
    clientThresholdData?.materialDelivery || [],
  );
  const deliveryByCls = new Map();
  clientDelivery.forEach((material) => {
    if (material?.cls) deliveryByCls.set(material.cls, material);
    if (material?.referenceName)
      deliveryByCls.set(material.referenceName, material);
  });

  const stableMaterials = buildStableMaterialCatalog({
    materialCatalog,
    materialDelivery: clientDelivery,
    t,
    locale,
    isHousewasteRegulation,
  });
  const materialHeaders = buildMaterialHeaderLabels(stableMaterials);
  const finesHeaderName = buildFinesHeaderName(clientThresholdCm, t);
  const structureHeaders = buildCsvStructureHeaders(t);

  const clientHasFines =
    (clientThresholdData?.totalFinesMass || 0) > 0 ||
    (clientThresholdData?.totalFinesVolume || 0) > 0 ||
    (clientThresholdData?.finesBelowThreshold?.count || 0) > 0 ||
    (clientThresholdData?.finesBelowThreshold?.massObject || 0) > 0 ||
    (clientThresholdData?.finesBelowThreshold?.volumeObject || 0) > 0;

  const finesMass =
    (clientThresholdData?.finesBelowThreshold?.massObject || 0) +
    (clientThresholdData?.totalFinesMass || 0);
  const finesVolume =
    (clientThresholdData?.finesBelowThreshold?.volumeObject || 0) +
    (clientThresholdData?.totalFinesVolume || 0);
  const finesCount = clientThresholdData?.finesBelowThreshold?.count || 0;

  // Always keep fines column for Excel column stability across exports
  const includeFinesColumn = true;

  const decreeParentCategories = decreeData?.parentCategoriesList || [];
  const decreeTotalWasteMass = decreeData?.totalWasteMass || 0;
  const decreeTotalMassRecoverable = decreeData?.totalMassRecoverable || 0;
  const decreeFinesMass =
    (decreeData?.finesBelowThreshold?.massObject || 0) +
    (decreeData?.totalFinesMass || 0);
  const decreeHasFines =
    decreeFinesMass > 0 ||
    (decreeData?.totalFinesVolume || 0) > 0 ||
    (decreeData?.finesBelowThreshold?.count || 0) > 0 ||
    (decreeData?.finesBelowThreshold?.volumeObject || 0) > 0;

  // The 11-99 recap has fixed DIB parent-category columns: not applicable to OMR.
  if (
    !isHousewasteRegulation &&
    decreeParentCategories.length > 0 &&
    decreeTotalWasteMass > 0
  ) {
    const massTabPercent = decreeParentCategories.map((cat) =>
      formatPercent(cat.massObjects, decreeTotalWasteMass),
    );
    const massTabKg = decreeParentCategories.map((cat) =>
      cat.massObjects
        ? ((cat.massObjects / decreeTotalWasteMass) * totalWeightKg).toFixed(4)
        : 0.0,
    );

    const otherIndex = (() => {
      const idx = decreeParentCategories.findIndex((cat) =>
        isOtherParentCategory(cat, t),
      );
      return idx !== -1 ? idx : decreeParentCategories.length - 1;
    })();

    const finesPercentForTab = formatPercent(
      decreeFinesMass,
      decreeTotalWasteMass,
    );
    const finesKgForTab = decreeFinesMass
      ? ((decreeFinesMass / decreeTotalWasteMass) * totalWeightKg).toFixed(4)
      : 0.0;

    const massTabPercentWithFines = [...massTabPercent];
    const massTabKgWithFines = [...massTabKg];
    if (includeFinesColumn || decreeHasFines) {
      massTabPercentWithFines.splice(otherIndex + 1, 0, finesPercentForTab);
      massTabKgWithFines.splice(otherIndex + 1, 0, finesKgForTab);
    }

    const totalMassRecoverablePercent = (
      (decreeTotalMassRecoverable / decreeTotalWasteMass) *
      100
    ).toFixed(2);
    // period.weight is already kg
    const totalWasteMassKg = totalWeightKg.toFixed(2);

    csvFiles.push({
      data: [
        buildMassTabHeaders({
          parentCategories: decreeParentCategories,
          t,
          hasFines: includeFinesColumn || decreeHasFines,
        }),
        [
          `${filenameBase}_TableauRecap_decret_11-99`,
          String(DECREE_1199_THRESHOLD_CM),
          `${totalMassRecoverablePercent}%`,
          ...massTabPercentWithFines,
          totalWasteMassKg,
          ...massTabKgWithFines,
        ],
      ],
      filename: `${filenameBase}_TableauRecap_decret1199${thresholdFilenameSuffix(DECREE_1199_THRESHOLD_CM)}.csv`,
    });
  }

  const totalWasteMass = clientThresholdData?.totalWasteMass || 0;
  const totalWasteVolume = clientThresholdData?.totalWasteVolume || 0;
  const totalMassRecoverable = clientThresholdData?.totalMassRecoverable || 0;
  const totalVolumeRecoverable =
    clientThresholdData?.totalVolumeRecoverable || 0;
  const materialTotal = clientThresholdData?.materialTotal || 0;
  const clientSuffix = thresholdFilenameSuffix(clientThresholdCm);

  if (stableMaterials.length > 0 && totalWasteMass > 0) {
    const values = stableMaterials.map((material) => {
      const match = findDeliveryMaterial(deliveryByCls, material.referenceName);
      return formatPercent(match?.massObject, totalWasteMass);
    });
    const finesPercent = formatPercent(finesMass, totalWasteMass);
    const recoverable = ((totalMassRecoverable / totalWasteMass) * 100).toFixed(
      2,
    );

    csvFiles.push({
      data: [
        [
          structureHeaders.mass,
          structureHeaders.cutoffThresholdCm,
          structureHeaders.recoverableMassPercent,
          ...materialHeaders,
          ...(includeFinesColumn || clientHasFines ? [finesHeaderName] : []),
        ],
        [
          `${container._id}_masse`,
          String(clientThresholdCm),
          `${recoverable}%`,
          ...values,
          ...(includeFinesColumn || clientHasFines ? [finesPercent] : []),
        ],
      ],
      filename: `${filenameBase}_masse${clientSuffix}.csv`,
    });
  }

  if (stableMaterials.length > 0 && totalWasteVolume > 0) {
    const values = stableMaterials.map((material) => {
      const match = findDeliveryMaterial(deliveryByCls, material.referenceName);
      return formatPercent(match?.volumeObject, totalWasteVolume);
    });
    const finesPercent = formatPercent(finesVolume, totalWasteVolume);
    const recoverable = (
      (totalVolumeRecoverable / totalWasteVolume) *
      100
    ).toFixed(2);

    csvFiles.push({
      data: [
        [
          structureHeaders.volume,
          structureHeaders.cutoffThresholdCm,
          structureHeaders.recoverableVolumePercent,
          ...materialHeaders,
          ...(includeFinesColumn || clientHasFines ? [finesHeaderName] : []),
        ],
        [
          `${container._id}_volume`,
          String(clientThresholdCm),
          `${recoverable}%`,
          ...values,
          ...(includeFinesColumn || clientHasFines ? [finesPercent] : []),
        ],
      ],
      filename: `${filenameBase}_volume${clientSuffix}.csv`,
    });
  }

  if (stableMaterials.length > 0 && materialTotal > 0) {
    const values = stableMaterials.map((material) => {
      const match = findDeliveryMaterial(deliveryByCls, material.referenceName);
      return formatPercent(match?.count, materialTotal);
    });
    const finesPercent = formatPercent(finesCount, materialTotal);

    csvFiles.push({
      data: [
        [
          structureHeaders.objectCount,
          structureHeaders.cutoffThresholdCm,
          structureHeaders.total,
          ...materialHeaders,
          ...(includeFinesColumn || clientHasFines ? [finesHeaderName] : []),
        ],
        [
          `${container._id}_nbObjets`,
          String(clientThresholdCm),
          materialTotal,
          ...values,
          ...(includeFinesColumn || clientHasFines ? [finesPercent] : []),
        ],
      ],
      filename: `${filenameBase}_nbObjets${clientSuffix}.csv`,
    });
  }

  return csvFiles;
};

/**
 * Multi-report CSV summary: same 4 files, one data row per report.
 * Identity columns: Date (AAAA_MM_JJ) + Producteur.
 */
export const buildCharacterizationCsvSummaryFiles = ({
  reportRows,
  t,
  locale,
  clientThresholdCm,
  materialCatalog = [],
}) => {
  // Homogeneous OMR selection only: a single DIB report keeps the classic columns.
  const isHousewasteRegulation =
    reportRows.length > 0 &&
    reportRows.every((row) => row.regulationType === REGULATION_TYPES.RHW);
  const effectiveThresholdCm = isHousewasteRegulation ? 0 : clientThresholdCm;

  const allClientDeliveries = reportRows.flatMap(
    (row) => row.clientThresholdData?.materialDelivery || [],
  );
  const stableMaterials = buildStableMaterialCatalog({
    materialCatalog,
    materialDelivery: aggregateMaterialDelivery(allClientDeliveries),
    t,
    locale,
    isHousewasteRegulation,
  });
  const materialHeaders = buildMaterialHeaderLabels(stableMaterials);
  const finesHeaderName = buildFinesHeaderName(effectiveThresholdCm, t);
  const structureHeaders = buildCsvStructureHeaders(t);
  const clientSuffix = thresholdFilenameSuffix(effectiveThresholdCm);
  const decreeSuffix = thresholdFilenameSuffix(DECREE_1199_THRESHOLD_CM);

  const massRows = [];
  const volumeRows = [];
  const countRows = [];
  const recapRows = [];
  let recapParentCategories = [];

  reportRows.forEach((report) => {
    const { dateLabel, producerName, period, clientThresholdData, decreeData } =
      report;
    const totalWeightKg = period?.weight ? Number(period.weight) : 0;

    const clientDelivery = aggregateMaterialDelivery(
      clientThresholdData?.materialDelivery || [],
    );
    const deliveryByCls = new Map();
    clientDelivery.forEach((material) => {
      if (material?.cls) deliveryByCls.set(material.cls, material);
      if (material?.referenceName)
        deliveryByCls.set(material.referenceName, material);
    });

    const finesMass =
      (clientThresholdData?.finesBelowThreshold?.massObject || 0) +
      (clientThresholdData?.totalFinesMass || 0);
    const finesVolume =
      (clientThresholdData?.finesBelowThreshold?.volumeObject || 0) +
      (clientThresholdData?.totalFinesVolume || 0);
    const finesCount = clientThresholdData?.finesBelowThreshold?.count || 0;

    const totalWasteMass = clientThresholdData?.totalWasteMass || 0;
    const totalWasteVolume = clientThresholdData?.totalWasteVolume || 0;
    const totalMassRecoverable = clientThresholdData?.totalMassRecoverable || 0;
    const totalVolumeRecoverable =
      clientThresholdData?.totalVolumeRecoverable || 0;
    const materialTotal = clientThresholdData?.materialTotal || 0;

    if (stableMaterials.length > 0 && totalWasteMass > 0) {
      massRows.push([
        dateLabel,
        producerName,
        String(effectiveThresholdCm),
        `${((totalMassRecoverable / totalWasteMass) * 100).toFixed(2)}%`,
        ...stableMaterials.map((material) =>
          formatPercent(
            findDeliveryMaterial(deliveryByCls, material.referenceName)
              ?.massObject,
            totalWasteMass,
          ),
        ),
        formatPercent(finesMass, totalWasteMass),
      ]);
    }

    if (stableMaterials.length > 0 && totalWasteVolume > 0) {
      volumeRows.push([
        dateLabel,
        producerName,
        String(effectiveThresholdCm),
        `${((totalVolumeRecoverable / totalWasteVolume) * 100).toFixed(2)}%`,
        ...stableMaterials.map((material) =>
          formatPercent(
            findDeliveryMaterial(deliveryByCls, material.referenceName)
              ?.volumeObject,
            totalWasteVolume,
          ),
        ),
        formatPercent(finesVolume, totalWasteVolume),
      ]);
    }

    if (stableMaterials.length > 0 && materialTotal > 0) {
      countRows.push([
        dateLabel,
        producerName,
        String(effectiveThresholdCm),
        materialTotal,
        ...stableMaterials.map((material) =>
          formatPercent(
            findDeliveryMaterial(deliveryByCls, material.referenceName)?.count,
            materialTotal,
          ),
        ),
        formatPercent(finesCount, materialTotal),
      ]);
    }

    const decreeParentCategories = decreeData?.parentCategoriesList || [];
    const decreeTotalWasteMass = decreeData?.totalWasteMass || 0;
    const decreeTotalMassRecoverable = decreeData?.totalMassRecoverable || 0;
    const decreeFinesMass =
      (decreeData?.finesBelowThreshold?.massObject || 0) +
      (decreeData?.totalFinesMass || 0);

    if (
      !isHousewasteRegulation &&
      decreeParentCategories.length > 0 &&
      decreeTotalWasteMass > 0
    ) {
      const massTabPercent = decreeParentCategories.map((cat) =>
        formatPercent(cat.massObjects, decreeTotalWasteMass),
      );
      const massTabKg = decreeParentCategories.map((cat) =>
        cat.massObjects
          ? ((cat.massObjects / decreeTotalWasteMass) * totalWeightKg).toFixed(
              4,
            )
          : 0.0,
      );
      const otherIndex = (() => {
        const idx = decreeParentCategories.findIndex((cat) =>
          isOtherParentCategory(cat, t),
        );
        return idx !== -1 ? idx : decreeParentCategories.length - 1;
      })();
      const massTabPercentWithFines = [...massTabPercent];
      const massTabKgWithFines = [...massTabKg];
      massTabPercentWithFines.splice(
        otherIndex + 1,
        0,
        formatPercent(decreeFinesMass, decreeTotalWasteMass),
      );
      massTabKgWithFines.splice(
        otherIndex + 1,
        0,
        decreeFinesMass
          ? ((decreeFinesMass / decreeTotalWasteMass) * totalWeightKg).toFixed(
              4,
            )
          : 0.0,
      );

      if (recapParentCategories.length === 0) {
        recapParentCategories = decreeParentCategories;
      }

      recapRows.push([
        dateLabel,
        producerName,
        String(DECREE_1199_THRESHOLD_CM),
        `${((decreeTotalMassRecoverable / decreeTotalWasteMass) * 100).toFixed(2)}%`,
        ...massTabPercentWithFines,
        totalWeightKg.toFixed(2),
        ...massTabKgWithFines,
      ]);
    }
  });

  const csvFiles = [];

  if (massRows.length > 0) {
    csvFiles.push({
      data: [
        [
          structureHeaders.date,
          structureHeaders.producer,
          structureHeaders.cutoffThresholdCm,
          structureHeaders.recoverableMassPercent,
          ...materialHeaders,
          finesHeaderName,
        ],
        ...massRows,
      ],
      filename: `recap_masse${clientSuffix}.csv`,
    });
  }

  if (volumeRows.length > 0) {
    csvFiles.push({
      data: [
        [
          structureHeaders.date,
          structureHeaders.producer,
          structureHeaders.cutoffThresholdCm,
          structureHeaders.recoverableVolumePercent,
          ...materialHeaders,
          finesHeaderName,
        ],
        ...volumeRows,
      ],
      filename: `recap_volume${clientSuffix}.csv`,
    });
  }

  if (countRows.length > 0) {
    csvFiles.push({
      data: [
        [
          structureHeaders.date,
          structureHeaders.producer,
          structureHeaders.cutoffThresholdCm,
          structureHeaders.total,
          ...materialHeaders,
          finesHeaderName,
        ],
        ...countRows,
      ],
      filename: `recap_nbObjets${clientSuffix}.csv`,
    });
  }

  if (recapRows.length > 0) {
    const recapHeaders = [
      structureHeaders.date,
      structureHeaders.producer,
      structureHeaders.cutoffThresholdCm,
      ...buildMassTabHeaders({
        parentCategories: recapParentCategories,
        t,
        hasFines: true,
      }).slice(2),
    ];
    csvFiles.push({
      data: [recapHeaders, ...recapRows],
      filename: `recap_TableauRecap_decret1199${decreeSuffix}.csv`,
    });
  }

  return csvFiles;
};

export const serializeCsvContent = (rows) => {
  const csvContent = rows
    .map((row) =>
      row
        .map((cell) => {
          if (
            typeof cell === "string" &&
            (cell.includes(";") || cell.includes("\n"))
          ) {
            return `"${cell}"`;
          }
          return cell;
        })
        .join(";"),
    )
    .join("\n");
  return `\uFEFF${csvContent}`;
};
